/**
 * Birth date validation utilities for healthcare applications
 * Provides comprehensive validation for date of birth input
 */

export interface BirthDateValidationResult {
  isValid: boolean;
  error?: string;
  age?: number;
}

export interface DateParts {
  day: number;
  month: number;
  year: number;
}

/**
 * Check if a year is a leap year
 * @param year - The year to check
 * @returns True if the year is a leap year, false otherwise
 */
export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/**
 * Get the number of days in a specific month/year
 * @param month - The month number (1-12)
 * @param year - The year
 * @returns The number of days in the specified month
 */
export function getDaysInMonth(month: number, year: number): number {
  const daysPerMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  if (month < 1 || month > 12) {
    return 0;
  }

  const days = daysPerMonth[month - 1];

  // Handle February in leap years
  if (month === 2 && isLeapYear(year)) {
    return 29;
  }

  return days;
}

/**
 * Calculate age from birth date
 * @param day - The day of birth (1-31)
 * @param month - The month of birth (1-12)
 * @param year - The year of birth
 * @returns The calculated age in years
 */
export function calculateAgeFromParts(
  day: number,
  month: number,
  year: number
): number {
  const today = new Date();
  const birthDate = new Date(year, month - 1, day);

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  // Adjust if birthday hasn't occurred this year
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
}

/**
 * Validate individual day field
 * @param day - The day value to validate
 * @param month - Optional month for context-specific validation
 * @param year - Optional year for context-specific validation
 * @returns Validation result with error message if invalid
 */
export function validateDay(
  day: number | undefined,
  month: number | undefined,
  year: number | undefined
): { isValid: boolean; error?: string } {
  if (day === undefined || day === null || isNaN(day)) {
    return { isValid: false, error: 'validation.day_required' };
  }

  if (day < 1 || day > 31) {
    return { isValid: false, error: 'validation.day_range' };
  }

  // If we have month and year, validate against actual days in that month
  if (month && year) {
    const maxDays = getDaysInMonth(month, year);
    if (day > maxDays) {
      return { isValid: false, error: 'validation.day_invalid_for_month' };
    }
  }

  return { isValid: true };
}

/**
 * Validate individual month field
 * @param month - The month value to validate (1-12)
 * @returns Validation result with error message if invalid
 */
export function validateMonth(
  month: number | undefined
): { isValid: boolean; error?: string } {
  if (month === undefined || month === null || isNaN(month)) {
    return { isValid: false, error: 'validation.month_required' };
  }

  if (month < 1 || month > 12) {
    return { isValid: false, error: 'validation.month_range' };
  }

  return { isValid: true };
}

/**
 * Validate individual year field
 * @param year - The year value to validate
 * @returns Validation result with error message if invalid
 */
export function validateYear(
  year: number | undefined
): { isValid: boolean; error?: string } {
  const currentYear = new Date().getFullYear();
  const minYear = 1900;

  if (year === undefined || year === null || isNaN(year)) {
    return { isValid: false, error: 'validation.year_required' };
  }

  if (year < minYear) {
    return { isValid: false, error: 'validation.year_too_old' };
  }

  if (year > currentYear) {
    return { isValid: false, error: 'validation.year_future' };
  }

  return { isValid: true };
}

/**
 * Comprehensive birth date validation
 * Returns validation result with age if valid
 * @param day - The day of birth (1-31)
 * @param month - The month of birth (1-12)
 * @param year - The year of birth
 * @returns Validation result with age if valid, or error message if invalid
 */
export function validateBirthDate(
  day: number | undefined,
  month: number | undefined,
  year: number | undefined
): BirthDateValidationResult {
  // Validate each field individually
  const dayValidation = validateDay(day, month, year);
  if (!dayValidation.isValid) {
    return { isValid: false, error: dayValidation.error };
  }

  const monthValidation = validateMonth(month);
  if (!monthValidation.isValid) {
    return { isValid: false, error: monthValidation.error };
  }

  const yearValidation = validateYear(year);
  if (!yearValidation.isValid) {
    return { isValid: false, error: yearValidation.error };
  }

  // All fields are present and individually valid at this point
  // Type-safe assertion - validation above ensures these are defined
  if (day === undefined || month === undefined || year === undefined) {
    return { isValid: false, error: 'validation.date_invalid' };
  }

  const d = day;
  const m = month;
  const y = year;

  // Verify the date is actually valid (handles edge cases)
  const testDate = new Date(y, m - 1, d);
  if (
    testDate.getFullYear() !== y ||
    testDate.getMonth() !== m - 1 ||
    testDate.getDate() !== d
  ) {
    return { isValid: false, error: 'validation.date_invalid' };
  }

  // Check if date is in the future
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  testDate.setHours(0, 0, 0, 0);

  if (testDate > today) {
    return { isValid: false, error: 'validation.date_future' };
  }

  // Calculate and validate age
  const age = calculateAgeFromParts(d, m, y);

  if (age < 0) {
    return { isValid: false, error: 'validation.date_future' };
  }

  if (age > 125) {
    return { isValid: false, error: 'validation.age_unrealistic' };
  }

  return { isValid: true, age };
}

/**
 * Convert date parts to ISO date string (YYYY-MM-DD) for FHIR
 * @param day - The day (1-31)
 * @param month - The month (1-12)
 * @param year - The year
 * @returns ISO 8601 formatted date string (YYYY-MM-DD)
 */
export function datePartsToISOString(
  day: number,
  month: number,
  year: number
): string {
  const d = String(day).padStart(2, '0');
  const m = String(month).padStart(2, '0');
  const y = String(year);

  return `${y}-${m}-${d}`;
}

/**
 * Parse ISO date string or Date object to date parts
 * @param date - The date to parse (ISO string or Date object)
 * @returns DateParts object with day, month, year, or null if invalid
 */
export function parseDateToParts(
  date: string | Date | null | undefined
): DateParts | null {
  if (!date) {
    return null;
  }

  let dateObj: Date;

  if (typeof date === 'string') {
    dateObj = new Date(date);
  } else {
    dateObj = date;
  }

  if (isNaN(dateObj.getTime())) {
    return null;
  }

  return {
    day: dateObj.getDate(),
    month: dateObj.getMonth() + 1,
    year: dateObj.getFullYear(),
  };
}
